import { Button } from "@/atoms-react/button/Button.react";
import { Base, Title } from "@/atoms-react/text/Text.react";
import { UserType } from "@/features/user/types";
import { formatNumber } from "@/features/utils/functions";
import { getWebBankingUrl } from "@/features/utils/web-banking-url";
import { ModalKYCIdentity } from "@/molecules/modal-kyc-identity/index.react";
import { setCreateAccount } from "@/redux/slices/transfertActionSlice";
import { Themes } from "@/types/theme";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { twMerge } from "tailwind-merge";

export const DiscoverOlkyCard = ({
  leUuid,
  auditPending = false,
  user
}: {
  leUuid: string | undefined;
  auditPending: boolean;
  user: UserType | undefined;
  openKYCModal: () => void;
}) => {
  const olkyCardsClassName =
    "relative overflow-hidden h-[188px] lg:h-[188px] xl::h-[223px] 2xl:h-[223px] rounded-sm w-full ";
  const olkyCardsClassName2 = "w-full h-full px-5 py-6 flex justify-center";
  const baseTextClassName = "text-white font-semibold mt-0 mb-2 z-10";
  const { t } = useTranslation(["pass"]);
  const [modalIdentity, setModalIdentity] = useState<boolean>(false);
  const dispatch = useDispatch();
  useEffect(() => {
    if (sessionStorage.getItem("DocumentQuizKyc")) {
      const data = JSON.parse(sessionStorage.getItem("DocumentQuizKyc") as string);
      if (data.quizIbanStep > 0) dispatch(setCreateAccount(true));
    }
  }, []);
  const redirectToBankAccount = () => {
    let email = user?.naturalPerson.AliasEmail.find((email) => email.Actif === true)?.Mail;
    if (!email && (user?.naturalPerson?.AliasEmail?.length as number) > 0) {
      email = user?.naturalPerson.AliasEmail[0].Mail;
    }
    const url = getWebBankingUrl(email);
    const success = window.open(url, "_blank");
    if (!success) document.location = url;
  };
  return (
    <>
      {!user?.naturalPerson?.codeShop && (
        <>
          <ModalKYCIdentity onClose={() => setModalIdentity(false)} open={modalIdentity} />
          <div
            className={twMerge(
              olkyCardsClassName,
              'relative overflow-hidden bg-[url("/images/olkypass/olkyPayConso.png")] bg-center bg-cover'
            )}
          >
            <div className="absolute right-0 bottom-4 z-0 flex w-52 items-center justify-center gap-x-3 lg:bottom-5 xl:right-0 xl:bottom-8 xl:w-52 2xl:right-0">
              <div className="overflow-hidden rounded-sm">
                <img src="/images/olkypay/cards/particular-card.png" alt="Individual card" />
              </div>
            </div>

            <div className={`${olkyCardsClassName2} items-left flex-col justify-between`}>
              <Base className={twMerge(baseTextClassName, "text-left")}>
                {t("olkypass.general.olky_account_conso")}
              </Base>
              {user?.naturalPerson.olkypay_account_requested === 1 ? (
                <Base className={twMerge("z-10 mt-2 mb-5 w-52 text-left text-white")}>
                  {" "}
                  {t("olkypass.general.olky_account_pending")}{" "}
                </Base>
              ) : (
                <div className="flex justify-start">
                  <Button
                    size="md"
                    theme={Themes.white}
                    dataCy="open-olky-account"
                    disabled={
                      (!!leUuid ||
                        auditPending ||
                        user?.naturalPerson.olkypay_account_requested === 1 ||
                        user?.naturalPerson.codeShop) as boolean
                    }
                    onClick={() => dispatch(setCreateAccount(true))}
                    iconPosition="before"
                    className="mt-6 h-8 w-48 border-none text-start text-black text-primary text-xs hover:bg-slate-50 sm:h-10 sm:w-80 sm:text-base md:w-96 lg:h-8 lg:w-80 lg:text-sm xl:h-10 xl:text-base xl:text-lg dark:bg-white dark:text-black dark:text-primary dark:hover:bg-slate-50"
                  >
                    {t("olkypass.mainPage.olkypass.bloc2.btn")}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </>
      )}
      {user?.naturalPerson?.codeShop && !leUuid && (
        <div
          className={twMerge(
            "relative w-full overflow-hidden rounded-sm bg-center bg-cover",
            olkyCardsClassName,
            "bg-[url('/images/olkypass/olkyPayConso.png')]"
          )}
        >
          {/* Gradient behind the card for consumer card*/}
          <div className="absolute right-0 bottom-4 z-0 flex w-52 items-center justify-center gap-x-3 lg:bottom-5 xl:right-0 xl:bottom-8 xl:w-52 2xl:right-0">
            {user?.naturalPerson.olkypay_account_requested !== 1 && (
              <div className="overflow-hidden rounded-sm">
                <img src="/images/olkypay/cards/particular-card.png" alt="Individual card" />
              </div>
            )}
          </div>{" "}
          <div className="flex h-full w-full flex-col p-4 md:p-6">
            <Base className={twMerge(baseTextClassName, "mb-0")}>
              {" "}
              {t("olkypass.general.olky_account_conso")}
            </Base>
            <Title className="z-0 mb-0 w-max rounded-xl text-white backdrop-blur">
              {formatNumber(
                parseFloat(
                  user.naturalPerson.olkyAccount?.balance
                    .replaceAll(" ", "")
                    .replaceAll(",", ".") as string
                )
              )}{" "}
              €
            </Title>
            <div className="mt-auto flex flex-1 items-end">
              <Button
                onClick={redirectToBankAccount}
                size="md"
                disabled={!!leUuid}
                theme={Themes.white}
                className="mt-4 h-8 w-64 border-none text-start text-black text-primary text-xs hover:bg-slate-50 sm:h-10 sm:w-80 sm:text-base md:w-96 lg:h-8 lg:w-80 lg:text-sm xl:h-10 xl:text-base xl:text-lg dark:bg-white dark:text-black dark:text-primary dark:hover:bg-slate-50"
                icon={<ArrowTopRightOnSquareIcon />}
              >
                {t("olkypass.mainPage.olkypass.bloc2.btn_access_olkypay")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
